/* ===================================================================
   SENIOR ASSOCIATE — external check API (graceful fallback).
   Hosts the lightweight client for the SA service configured via
   VITE_SA_BASE_URL, plus the hook/SA pill UI that surfaces health.
   When the env var is unset the SA integration is "not configured":
   health polling is skipped and all calls no-op.
=================================================================== */
import { useEffect, useState } from "react";
import type { SimEmail, MemoFieldValue } from "./types";

export const SA_BASE: string | null =
  ((import.meta.env && (import.meta.env as any).VITE_SA_BASE_URL) || "").replace(/\/+$/, "") ||
  null;

/* Thin GET/POST helpers for the SA service's persistence endpoints
 * (/playbook, /runs, /config). All fail soft → null so the UI degrades
 * gracefully when the service is offline or not configured. */
export async function saFetch(path: string, opts?: RequestInit): Promise<any> {
  if (!SA_BASE) return null;
  const ctrl = new AbortController();
  const tid = setTimeout(() => ctrl.abort(), 3000);
  try {
    const r = await fetch(SA_BASE + path, { ...(opts || {}), signal: ctrl.signal });
    clearTimeout(tid);
    if (!r.ok) return null;
    return await r.json();
  } catch (_e) {
    clearTimeout(tid);
    return null;
  }
}
export const saGet = (path: string) => saFetch(path);
export const saPost = (path: string, body?: any) =>
  saFetch(path, {
    method: "POST",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
export const saPut = (path: string, body: any) =>
  saFetch(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export type SAStatus = "checking" | "online" | "offline" | "not_configured";

export interface UseSeniorAssociateOpts { withDetails?: boolean }
export interface UseSeniorAssociateResult {
  status: SAStatus;
  info: any | null;
  playbook: any | null;
  runs: any[] | null;
  refresh: () => void;
}

export function useSeniorAssociate(opts?: UseSeniorAssociateOpts): UseSeniorAssociateResult {
  const withDetails = !!(opts && opts.withDetails);
  const [status, setStatus] = useState<SAStatus>(SA_BASE ? "checking" : "not_configured");
  const [info, setInfo] = useState<any | null>(null);
  const [playbook, setPlaybook] = useState<any | null>(null);
  const [runs, setRuns] = useState<any[] | null>(null);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((t) => t + 1);
  useEffect(() => {
    if (!SA_BASE) {
      setStatus("not_configured");
      return;
    }
    let alive = true;
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 1200);
    fetch(SA_BASE + "/health", { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
      .then((j) => {
        if (!alive) return;
        clearTimeout(tid);
        setStatus("online");
        setInfo(j);
      })
      .catch(() => {
        if (!alive) return;
        clearTimeout(tid);
        setStatus("offline");
      });
    return () => {
      alive = false;
      clearTimeout(tid);
      ctrl.abort();
    };
  }, [tick]);
  // When asked for details and the service is up, pull the live (persisted)
  // playbook and recent run history straight from the SA service.
  useEffect(() => {
    if (!withDetails || status !== "online") {
      return;
    }
    let alive = true;
    saGet("/playbook").then((p) => {
      if (alive && p) setPlaybook(p);
    });
    saGet("/runs?limit=8").then((r) => {
      if (alive && Array.isArray(r)) setRuns(r);
    });
    return () => {
      alive = false;
    };
  }, [withDetails, status, tick]);
  return { status, info, playbook, runs, refresh };
}

export interface SACheckPayload {
  memo: Record<string, any>;
  documents: any[];
  working_group: Array<{ name: string; role: string }>;
}
export interface SACheckResponse {
  ok: boolean;
  response?: any;
  error?: string;
}

export async function callSeniorAssociate(payload: SACheckPayload): Promise<SACheckResponse> {
  if (!SA_BASE) return { ok: false, error: "SA service not configured (VITE_SA_BASE_URL)" };
  try {
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 3000);
    const r = await fetch(SA_BASE + "/check", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: ctrl.signal,
    });
    clearTimeout(tid);
    if (!r.ok) return { ok: false, error: "HTTP " + r.status };
    const j = await r.json();
    return { ok: true, response: j };
  } catch (e: any) {
    return { ok: false, error: String((e && e.message) || e) };
  }
}

/* App doc_type (normalized lowercase) → Senior Associate API DocType enum. */
export const SA_DOC_TYPE: Record<string, string> = {
  charter: "Charter",
  spa: "SPA",
  ira: "IRA",
  side_letter: "SideLetter",
  voting_agreement: "VotingAgreement",
  rofr: "ROFRCoSale",
  cap_table: "CapTable",
  term_sheet: "TermSheet",
};
export const saDocType = (t: string): string => SA_DOC_TYPE[t] || "Other";

/* Deal-memo field key → DealMemo schema field. */
export const SA_MEMO_MAP: Record<string, string> = {
  issuer: "company",
  company: "company",
  deal_type: "deal_type",
  our_role: "our_role",
  round_size: "round_size",
  pre_money_valuation: "pre_money",
  pre_money: "pre_money",
  post_money_valuation: "post_money",
  post_money: "post_money",
  price_per_share: "price_per_share",
  option_pool: "option_pool_pct_post",
  option_pool_pct_post: "option_pool_pct_post",
  lead_investor: "lead_investor",
  board_composition: "board_composition",
  liquidation_preference: "liquidation_preference",
  anti_dilution: "anti_dilution",
  dividend_rate: "dividend_rate",
  governing_law: "governing_law",
  target_close: "target_close",
};

/* Build a POST /check payload from an email's attachments + the current deal state. */
export function buildSAPayload(
  email: SimEmail,
  memoFields: Record<string, MemoFieldValue | string> | null | undefined,
): SACheckPayload {
  const memo: Record<string, any> = {};
  Object.entries(memoFields || {}).forEach(([k, v]) => {
    const dk = SA_MEMO_MAP[k];
    const val = v && typeof v === "object" ? (v as MemoFieldValue).value : v;
    if (!dk || val == null || val === "") return;
    if (dk === "option_pool_pct_post") {
      const m = String(val).match(/(\d+(?:\.\d+)?)/);
      if (m) memo[dk] = parseFloat(m[1]);
    } // schema: numeric percent
    else memo[dk] = String(val);
  });
  const documents = (email.attachments || []).map((a, i) => ({
    id: email.id + "-" + i,
    name: a.filename,
    doc_type: saDocType(a.doc_type),
    version: "v" + (a.version || 1),
    is_latest: true,
    text_excerpt: String(a.content || "").slice(0, 2000),
  }));
  const parties: Array<{ name: string; role: string }> = [];
  const seen = new Set<string>();
  const add = (addr: string, role: string) => {
    const name = String(addr || "").trim();
    if (!name || seen.has(name)) return;
    seen.add(name);
    parties.push({ name, role });
  };
  add(email.from, "sender");
  (Array.isArray(email.to) ? email.to : String((email as any).to || "").split(/[,;]+/)).forEach(
    (t: string) => add(t, "recipient"),
  );
  (Array.isArray(email.cc) ? email.cc : String((email as any).cc || "").split(/[,;]+/)).forEach(
    (t: string) => add(t, "cc"),
  );
  return { memo, documents, working_group: parties };
}

/* Collapse a CheckResponse into a single senior_associate check {conf,out}.
 * conf mirrors the pre-baked convention: ~0.96 = clean (pass/green), below τ=0.60
 * = surfaced as a flag. FAIL / Critical/High severity drive conf down so the
 * finding surfaces regardless of τ (per the check's threshold rationale). */
export interface SASummary {
  conf: number;
  out: string;
  runId: string | null;
}
export function summarizeSA(resp: any): SASummary {
  const sm = resp.summary || {};
  const checks = resp.checks || [];
  const fail = sm.verdict_fail || 0;
  const warn = sm.verdict_warn || 0;
  const pass = sm.verdict_pass || 0;
  const crit = (sm.severity_critical || 0) + (sm.severity_high || 0);
  const conf = fail || crit ? 0.2 : warn ? 0.5 : 0.96;
  const flagged = checks
    .filter((c: any) => c.verdict === "FAIL" || c.verdict === "WARN")
    .sort((a: any, b: any) => (b.confidence || 0) - (a.confidence || 0));
  const detail = flagged
    .slice(0, 3)
    .map(
      (c: any) =>
        c.verdict + " · " + c.name + " [" + c.severity + "] — " + c.finding,
    )
    .join("  ·  ");
  const out =
    "Senior Associate (live) — " +
    pass +
    " pass / " +
    warn +
    " warn / " +
    fail +
    " fail across " +
    (sm.total || checks.length) +
    " checks. " +
    (detail || "No cross-document inconsistencies found.");
  return { conf, out, runId: resp.run_id || null };
}
